// The authored-HTML PARSER, split out of DocHtml.tsx (2026-08-06).
//
// The renderer, the plain-text conversion the copy buttons paste, the segment
// colour pass and the e-mail split all need the same tree. Leaving the parser
// inside the renderer module forced every one of them to import a React
// component, and the interactive leaves (copy field, timeline) needed the
// plain-text conversion, which needed the parser: a cycle. The parser has no
// imports at all, so nothing can close a loop through it.
//
// Behaviour is unchanged from the version that shipped 2026-08-03; the
// comments that record WHY each tolerance exists travel with the code.
//
// The ONE import is `shared/doc-wrapper`, which itself imports nothing, so the
// "no cycle can close through the parser" property still holds. It is shared
// rather than copied because the Worker repairs a lost `<doc>` on the way in
// and the reader recognises one on the way out: two copies of that predicate
// would eventually disagree about the same document.

import { looksLikeDocSource } from "../../shared/doc-wrapper";

export type DocAttrs = Record<string, string>;
export type DocNode =
  | { kind: "text"; text: string }
  | { kind: "el"; tag: string; attrs: DocAttrs; children: DocNode[] };

/**
 * Only `<br>` is genuinely childless. Everything else may be written either
 * self-closing with attributes (`<stat label="x" value="y"/>`) or with a body
 * (`<q>„Už nevím, čemu věřit."</q>`); treating the second form as void drops
 * its text into the parent and the block renders empty. Caught by looking at
 * the rendered document, not by the tests, which asserted structure.
 */
const VOID_TAGS = new Set(["br"]);

/**
 * Tags that never nest inside themselves. An unclosed one closes when the next
 * sibling opens, the way a browser treats `<li>`, so a model that forgets
 * `</item>` still produces a flat list instead of a staircase.
 */
const AUTO_CLOSE_SIBLING = new Set([
  "stat", "swatch", "meter", "step", "row", "item", "q", "pos", "neg",
  "card", "col", "chapter", "seg", "beat", "email", "phase", "tl", "alt",
  "fstage", "ph", "mi", "aud", "tariff"
]);

/**
 * Tags a paragraph may contain without ceasing to be a paragraph. `term` and
 * `gap` belong here: both are inline by nature, and leaving them out would
 * make a sentence containing one render as a bare div, which is invisible to
 * the walk annotation (`ANCHOR_BLOCK_SELECTOR` anchors on `p`).
 */
export const INLINE_TAGS = new Set(["b", "strong", "i", "em", "mark", "code", "br", "term", "gap", "ref"]);

/**
 * A document whose `<doc>` wrapper was lost is still THIS language, and
 * reading it as markdown is what printed `<p>` and `<lead>` as visible text on
 * the client's screen (the incident is written up in shared/doc-wrapper.ts).
 * The predicate is shared with the Worker, which repairs the wrapper where a
 * document is stored; this is the reader's half, so an already-stored document
 * renders as its author wrote it.
 */
export function isDocHtml(source: string): boolean {
  return looksLikeDocSource(source);
}

function decodeEntities(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");
}

function parseAttrs(raw: string): DocAttrs {
  const attrs: DocAttrs = {};
  const re = /([a-zA-Z][\w-]*)\s*=\s*"([^"]*)"|([a-zA-Z][\w-]*)\s*=\s*'([^']*)'|([a-zA-Z][\w-]*)(?=[\s/]|$)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw))) {
    const name = (match[1] ?? match[3] ?? match[5] ?? "").toLowerCase();
    if (!name) continue;
    // Event handlers and style are never read; keeping them out of the tree
    // means a stray onclick cannot even be inspected downstream.
    if (name.startsWith("on") || name === "style") continue;
    attrs[name] = decodeEntities(match[2] ?? match[4] ?? "");
  }
  return attrs;
}

/**
 * Tolerant tokenizer. Real model output has stray whitespace, occasional
 * unclosed tags and text containing `<`; none of those may lose content, so an
 * unmatched close tag is ignored and anything still open at EOF is closed.
 */
export function parseDocHtml(source: string): DocNode[] {
  const root: DocNode = { kind: "el", tag: "#root", attrs: {}, children: [] };
  const stack: Array<Extract<DocNode, { kind: "el" }>> = [root as Extract<DocNode, { kind: "el" }>];
  const tagRe = /<(\/)?([a-zA-Z][\w-]*)((?:"[^"]*"|'[^']*'|[^>])*?)(\/)?>/g;
  let cursor = 0;
  const pushText = (text: string) => {
    if (!text) return;
    const decoded = decodeEntities(text);
    if (!decoded.trim() && !decoded.includes(" ")) return;
    stack[stack.length - 1].children.push({ kind: "text", text: decoded });
  };
  let match: RegExpExecArray | null;
  while ((match = tagRe.exec(source))) {
    pushText(source.slice(cursor, match.index));
    cursor = tagRe.lastIndex;
    const [, closing, rawTag, rawAttrs, selfClosing] = match;
    const tag = rawTag.toLowerCase();
    // Pop back to the nearest open element with this tag. Used both to close a
    // tag and to auto-close an unclosed sibling. A tag with no matching open is
    // IGNORED rather than unwinding the document: one stray `</p>` must not
    // swallow everything after it.
    const closeNearest = () => {
      for (let i = stack.length - 1; i > 0; i -= 1) {
        if (stack[i].tag === tag) {
          stack.length = i;
          return;
        }
      }
    };
    if (closing) {
      closeNearest();
      continue;
    }
    if (AUTO_CLOSE_SIBLING.has(tag)) closeNearest();
    const node: Extract<DocNode, { kind: "el" }> = {
      kind: "el",
      tag,
      attrs: parseAttrs(rawAttrs ?? ""),
      children: []
    };
    stack[stack.length - 1].children.push(node);
    if (!selfClosing && !VOID_TAGS.has(tag)) stack.push(node);
  }
  pushText(source.slice(cursor));
  return root.children;
}

/** Text content of a subtree, used where a tag's body is a plain string. */
export function docText(nodes: DocNode[]): string {
  return nodes
    .map((node) => (node.kind === "text" ? node.text : docText(node.children)))
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Attributes that carry NO reading text: DOM handles, layout ratios, colour
 * keys, flags. Everything else in this language is a label the renderer prints
 * (title, takeaway, label, value, note, name, left, right, cite, caption...),
 * so the default is to INCLUDE it and the exceptions are listed here.
 */
const STRUCTURAL_ATTRS = new Set([
  "id", "seg", "ratio", "cols", "use", "auto", "kind", "hex", "src", "frame",
  "show", "key", "n", "num", "at", "start", "min", "now", "covers", "done", "mark"
]);

/**
 * The document as a READER sees it: every text node plus the attribute values
 * the renderer prints, and not one character of markup.
 *
 * `docText` cannot serve here - it walks only text nodes, so a chapter whose
 * `title` changed would come back identical and the change would vanish.
 *
 * Written for the version diff (Tomas Pohl, 2026-09-02): the diff used to
 * split the RAW SOURCE into words, so a changed chapter printed
 * `<chapter title="Velka title="Nosna myslenka" takeaway=...` at the partner -
 * two versions of one attribute welded together by the word diff. The data was
 * never broken; only this view was. A word diff over reading text keeps the
 * precision and shows sentences instead of tags.
 */
export function docPlainText(nodes: DocNode[]): string {
  const parts: string[] = [];
  const walk = (list: DocNode[]): void => {
    for (const node of list) {
      if (node.kind === "text") {
        if (node.text.trim()) parts.push(node.text);
        continue;
      }
      for (const [name, value] of Object.entries(node.attrs)) {
        if (!STRUCTURAL_ATTRS.has(name) && value.trim()) parts.push(value);
      }
      walk(node.children);
    }
  };
  walk(nodes);
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

export function childrenOf(node: DocNode, tag: string): Array<Extract<DocNode, { kind: "el" }>> {
  if (node.kind !== "el") return [];
  return node.children.filter(
    (child): child is Extract<DocNode, { kind: "el" }> => child.kind === "el" && child.tag === tag
  );
}

export function isInlineOnly(nodes: DocNode[]): boolean {
  return nodes.every((node) => node.kind === "text" || INLINE_TAGS.has(node.tag));
}

/**
 * An `id` becomes a DOM handle the timeline calls `getElementById` with, and
 * the source is model output. Anything but a plain slug is dropped: the fold
 * still renders, it just cannot be jumped to, which the timeline then reflects
 * by drawing text instead of a button.
 *
 * ONE rule, three readers: the renderer that writes the attribute, the context
 * builder that lists which ids exist, and the e-mail split that carries the id
 * onto its section. Two copies of this would drift into a beat that links to a
 * section nobody gave an id.
 */
export function safeDocId(raw: string | undefined): string | undefined {
  const value = (raw ?? "").trim();
  return /^[A-Za-z][\w-]{0,63}$/.test(value) ? value : undefined;
}

/**
 * The DOM id an `<email n="01">` card carries: `email-01`. ONE rule, four
 * readers: the renderer that writes the id, the context builder that lists
 * which targets exist, the campaign map rows and the parity report. A second
 * derivation would drift into a map that points at cards nobody rendered.
 */
export function emailCardId(n: string | undefined): string | undefined {
  const value = (n ?? "").trim();
  return value ? safeDocId(`email-${value}`) : undefined;
}

/**
 * The DOM id an evening `<ph n="1">` section carries: `ph-1`. Same ONE-rule
 * law as `emailCardId`: the renderer writes it, the context builder lists it,
 * the evening axis scrolls to it and `<ref>`/`<fstage to>` may target it.
 * Unlike the e-mail map there is no parity to check: the axis and the section
 * render from the SAME node, so a dead axis segment cannot be authored.
 */
export function phaseCardId(n: string | undefined): string | undefined {
  const value = (n ?? "").trim();
  return value ? safeDocId(`ph-${value}`) : undefined;
}

/** Every element in a tree, depth first, for structural checks. */
export function docElements(nodes: DocNode[]): Array<Extract<DocNode, { kind: "el" }>> {
  const out: Array<Extract<DocNode, { kind: "el" }>> = [];
  const visit = (node: DocNode) => {
    if (node.kind !== "el") return;
    out.push(node);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return out;
}
